import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Paper
} from "@mui/material";
import { Download, Visibility, Payments, CalendarMonth, AccessTime } from "@mui/icons-material";
import api from "../../api/axios";

export default function MyPayslips() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPayslips();
  }, []);

  const loadPayslips = async () => {
    try {
      const res = await api.get("/payroll/history/me");
      setData(res.data || []);
    } catch (e) {
      console.error(e);
      alert("Failed to pull active payslip assets logs data.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPayslip = async (id) => {
    const res = await api.get(`/payroll/payslip/download/${id}`, {
      responseType: "blob"
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Payslip_Statement_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const previewPayslip = async (id) => {
    const res = await api.get(`/payroll/payslip/download/${id}`, {
      responseType: "blob"
    });
    const fileURL = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(fileURL, "_blank");
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="h4" fontWeight={800} color="#0f172a" gutterBottom>
        My E-Payslips Ledger
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Access, review, and download digital copies of your compensation reports and tax history.
      </Typography>

      <Grid container spacing={3}>
        {data.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <Card 
              sx={{ 
                borderRadius: 4, 
                border: "1px solid #e2e8f0", 
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)",
                transition: "transform 0.2s, box-shadow 0.2s",
                '&:hover': { transform: "translateY(-2px)", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)" }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center" color="primary.main">
                    <CalendarMonth fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                      Statement Cycle
                    </Typography>
                  </Stack>
                  <Chip 
                    label={p.status || "ISSUED"} 
                    size="small" 
                    color={p.status === "PAID" || p.status === "GENERATED" ? "success" : "warning"} 
                    sx={{ fontWeight: "bold", borderRadius: "6px" }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Cycle bounds window range:
                </Typography>
                <Typography variant="body1" fontWeight={600} color="#1e293b" mb={2}>
                  {p.cycleStartDate} ➜ {p.cycleEndDate}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary" mb={0.5}>
                      <AccessTime sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={600}>Days Worked</Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight={700} color="#334155">
                      {p.actualWorkedDays || "0"} Active Days
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary" mb={0.5}>
                      <Payments sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={600}>Net Take Home</Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight={800} color="#16a34a">
                      ₹ {Math.round(p.netSalary).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Visibility />}
                    onClick={() => previewPayslip(p.id)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
                  >
                    View Securely
                  </Button>

                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Download />}
                    onClick={() => downloadPayslip(p.id)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
                  >
                    Download
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {data.length === 0 && (
        <Paper sx={{ p: 5, textAlign: "center", borderRadius: 4, mt: 2 }} elevation={0}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
            No payroll processing cycles logged under this employee reference card file.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}